import { useAuthStore } from "../../stores/auth-store";
import React, { useEffect, useState } from "react";
import { companyUseCase } from "../../../di/container";
import { LucideHamburger, LucideMenu } from "lucide-react";
import { NotificationBell } from "./NotificationBell";

interface AdminHeaderProps {
  onLogoClick?: () => void;
  showMyPageLink?: boolean;
  mobileMenuToggleHandler?: () => void | Promise<void>;
}

export const AdminHeader: React.FC<AdminHeaderProps> = ({
  onLogoClick,
  showMyPageLink = true,
  mobileMenuToggleHandler
}) => {
  const { user } = useAuthStore();
  const [companyLogoUrl, setCompanyLogoUrl] = useState<string | null>(null);
  const defaultLogoUrl = "/assets/logo/simbol_logo.png";

  const [companyShopId, setCompanyShopId] = useState<number | null>(null);
  const [companyDomain, setCompanyDomain] = useState<string | null>(null);
  // 지원사업 고지 문구 — 어드민이 설정한 자유 텍스트(ACHIEVEMENT_NOTICE 참여). 있으면 배너 표시.
  const [disclosureText, setDisclosureText] = useState<string | null>(null);
  // 사이트 운영 종료 예정일. 예약된 회사만 값이 있다.
  const [closingScheduledAt, setClosingScheduledAt] = useState<string | null>(null);

  useEffect(() => {
    const fetchCompanyInfo = async () => {
      if (user?.companyId) {
        try {
          const companyInfo = await companyUseCase.getCompanyInfo({
            companyId: user.companyId,
          });
          setCompanyShopId(companyInfo.shopId ?? null);
          setCompanyDomain(companyInfo.domain ?? null);
          setCompanyLogoUrl(companyInfo.logoUrl ?? "");
          setDisclosureText(companyInfo.disclosureText ?? null);
          setClosingScheduledAt(companyInfo.closingScheduledAt ?? null);
        } catch (error) {
          console.error("Failed to fetch company info:", error);
          setCompanyLogoUrl(null);
        }
      }
    };

    fetchCompanyInfo();
  }, [user?.companyId]);

  const [isMiniMenuOpen, setIsMiniMenuOpen] = useState(false);
  const handleLogoClick = () => {
    if (onLogoClick) {
      onLogoClick();
    } else {
      // 기본적으로 관리자 페이지로 이동
      window.location.href = `/`;
    }
  };

  const { isAuthenticated, logout, isLoading } = useAuthStore();
  // 새 주문 알림은 회사 관리자·매니저에게 쌓인다 (백엔드 findMesMembersByCompanyId 와 같은 대상)
  const showNotificationBell =
    !!user?.companyId && (user.role === "COMPANY_ADMIN" || user.role === "COMPANY_MANAGER");

  return (
    <div className="w-full">
      {/*
        종료가 걸리면 매니저를 포함한 소속 구성원 모두가 알아야 해서 고지 줄과 같은 자리에 띄운다.
        지원사업 고지보다 급한 내용이라 위에 둔다.
      */}
      {closingScheduledAt ? (
        <div className="px-5 py-2.5 bg-[#FFF8E6] border-b border-[#F0DFB0] w-full">
          <div className="flex items-center gap-3 flex-wrap text-start">
            {/* 색보다 남은 일수가 먼저 눈에 들어와야 한다. */}
            <span className="shrink-0 px-2.5 py-1 rounded-[6px] bg-[#B26A00] text-white text-[12px] font-bold">
              {formatClosingDday(closingScheduledAt)}
            </span>
            <span className="text-[12.5px] text-[#7A5B00] leading-relaxed">
              <b className="font-bold">
                {formatClosingDate(closingScheduledAt)} 사이트 운영이 종료됩니다.
              </b>{" "}
              종료 후에는 주문과 자료를 조회할 수 없으니 필요한 자료를 미리 내려받아 주세요.
            </span>
          </div>
        </div>
      ) : null}

      {disclosureText ? (
        <div className="px-5 py-3 bg-[#4285F4] text-white font-medium flex flex-col text-start text-[12px] w-full">
          <div className="whitespace-pre-line">{disclosureText}</div>
        </div>
      ) : null}

      <header className="bg-white border-b border-gray-200 h-14 relative">
        <div className="max-w-full mx-auto h-full">
          <div className="flex items-center justify-between h-full">
            <div className="flex items-center">
              <button
                onClick={handleLogoClick}
                className="flex items-center hover:opacity-80 transition-opacity cursor-pointer ml-6"
              >
                {user?.role !== "ADMIN" && companyLogoUrl === null ? (
                  <></>
                ) : (
                  <>
                    <img
                      src={
                        companyLogoUrl
                          ? companyLogoUrl
                          : defaultLogoUrl
                      }
                      alt="Company Logo"
                      className="h-12 w-16 object-contain"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = defaultLogoUrl;
                      }}
                    />
                  </>
                )}
              </button>
            </div>

            <div className="z-[5000] relative">
              {isMiniMenuOpen && (
                <div
                  className={`drop-shadow-[0_0px_2px_rgba(0,0,0,0.1)]
                text-[#757575] bg-white / absolute right-[75px] md:right-9 top-6
                / h-auto / rounded-[0.5rem] / flex flex-col text-[14px]
                / border border-[#D9D9D9] transition-opacity duration-100
                ${isMiniMenuOpen ? "opacity-100" : "opacity-0 pointer-events-none"}
                min-w-[100px]`}
                >
                  {/* 첫 번째 요소 -> rounded-t-[0.5rem] */}
                  <div
                    className="px-5 p-2 / hover:bg-[#F2F2F2] / transition-colors
                    / border-b border-gray-100 / rounded-t-[0.5rem]"
                    role="button"
                    onClick={() => {
                      window.location.href = "/profile";
                    }}
                  >
                    회원 정보
                  </div>
                  {user?.companyId && <div
                    className="px-5 p-2 / hover:bg-[#F2F2F2] / transition-colors
                    / border-b border-gray-100"
                    role="button"
                    onClick={() => {
                      window.location.href = "/company/" + user?.companyId;
                    }}
                  >
                    업체 관리
                  </div>}
                  {user?.role === "ADMIN" && <div
                    className="px-5 p-2 / hover:bg-[#F2F2F2] / transition-colors
                    / border-b border-gray-100"
                    role="button"
                    onClick={() => {
                      window.location.href = "/admin/";
                    }}
                  >
                    관리자
                  </div>}
                  {companyShopId && companyDomain && (
                    <a
                      className="px-5 p-2 / hover:bg-[#F2F2F2] / transition-colors
                    / border-b border-gray-100"
                      role="button"
                      href={"https://" + companyDomain}
                      target="_blank"
                    >
                      쇼핑몰 홈
                    </a>
                  )}
                  {/* 마지막 요소 -> rounded-b-[0.5rem] */}
                  <div
                    className="px-5 p-2 / hover:bg-[#F2F2F2] / transition-colors
                    / border-b border-gray-100 / rounded-b-[0.5rem]"
                    role="button"
                    onClick={async () => {
                      await logout();
                      window.location.href = "/";
                    }}
                  >
                    로그아웃
                  </div>
                </div>
              )}
              {showMyPageLink && (
                <div className="flex items-center h-14 pr-6">
                  {showNotificationBell && (
                    // 알림 아이콘과 이름 사이를 얇은 구분선으로 나눠 두 요소의 경계를 분명히 한다
                    <div className="flex items-center pr-3 mr-3 border-r border-[#EEEEEE] h-5">
                      <NotificationBell companyId={user!.companyId!} />
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => setIsMiniMenuOpen(!isMiniMenuOpen)}
                    className="flex items-center gap-1.5 h-8 text-sm font-normal text-gray-700 hover:text-[#4285F4] transition-colors"
                    style={{ fontFamily: "Pretendard" }}
                    aria-expanded={isMiniMenuOpen}
                  >
                    {user?.name} 님
                    <svg
                      width="7"
                      height="5"
                      viewBox="0 0 8 6"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden="true"
                    >
                      <path d="M4 5.5L0.5 0.5L7.5 0.5L4 5.5Z" fill="currentColor" />
                    </svg>
                  </button>
                  <div className="md:hidden">
                    {mobileMenuToggleHandler && (
                      <div role="button" className="scale-[0.9] ml-2" onClick={mobileMenuToggleHandler}>
                        <LucideMenu />
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>
    </div>
  );
};

/** 남은 일수. 당일이면 D-DAY */
const formatClosingDday = (value: string): string => {
  const target = new Date(value).getTime();
  if (Number.isNaN(target)) return "종료 예정";

  const days = Math.max(0, Math.ceil((target - Date.now()) / (1000 * 60 * 60 * 24)));
  return days === 0 ? "D-DAY" : `D-${days}`;
};

/** "2026년 9월 21일" 형태. 시각까지는 필요 없다. */
const formatClosingDate = (value: string): string => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "예정일";
  return `${date.getFullYear()}년 ${date.getMonth() + 1}월 ${date.getDate()}일`;
};
